import { useCallback } from 'react';
import type { GoodsFirstPageRenderingModel } from '../../../../api/types';
import { bridge } from '../../../bridge';
import { getWebBaseURL } from '../../../getWebBaseURL';
import {
  appendParams,
  buildUrl,
  finalizeUrl,
  getDefaultDeeplink,
} from '../../utils';
import { useExperimentGroup } from '../../../../context/UserContext';
import { isWeb } from '../../../isWeb';
import { isIOS } from '../../../isIOS';
import { useRootStackNavigation } from '../../../../hooks/useRootNavigation';
import { useInstanceId, useThrottle } from '../../../../hooks';
import { isNotNil } from '../../../isNotNil';
import { useAnalyticsContext } from '../../../../context/AnalyticsContext';

type MoveToGoodsProps = {
  sno: number;
  firstPageRendering?: GoodsFirstPageRenderingModel | null;
  loggingAnalytics?: Record<string, unknown>;
  couponSno?: number;
  alwaysRN?: boolean;
  remoteDeeplink?: string;
  ad?: object | null;
};

const DEBUG_MODE = __DEV__;

// moveToGoods 중복 진입 방지 throttle 값. useThrottle(leading-edge, per-instance) 로 감싸며,
// 네이티브 상품 셀 탭 스로틀과 정합을 맞춘다(둘 다 leading-edge · 셀 인스턴스 단위):
//   - iOS: ProductCell 의 `.throttle(.milliseconds(700), latest: false)` → 700ms
//   - Android: `clickableThrottle`(=`throttleFirst(300L)`) → 300ms
// web 은 네이티브 중복진입 이슈가 없어 Android 값(300ms)에 맞춘다.
const MOVE_TO_GOODS_THROTTLE_MS = isIOS ? 700 : 300;

export const useMoveToGoods = () => {
  const isGoodsDetailRN = useExperimentGroup({
    sno: isWeb ? 'EXP1615' : `EXP1577`,
    targetGroups: ['B'],
  });

  const navigation = useRootStackNavigation();
  const instanceId = useInstanceId();
  // 상세 진입 시 출처 화면(FROM)으로 쓸 현재 화면명
  const { screen } = useAnalyticsContext();

  const moveToGoods = useCallback(
    ({
      firstPageRendering,
      sno,
      loggingAnalytics,
      couponSno,
      alwaysRN,
      remoteDeeplink,
      ad,
    }: MoveToGoodsProps) => {
      const experimentGroup = isGoodsDetailRN || alwaysRN || DEBUG_MODE;
      const getDeeplink = () => {
        if (experimentGroup) {
          const url = getDefaultDeeplink();
          appendParams(url, 'screen', 'GoodsDetail');
          return url;
        }

        return buildUrl(`ably://goods/${sno}`);
      };
      // 출처 화면(FROM) from-context를 logging_analytics로 전달한다.
      // 검색 만족도 등 dbt 지표가 prev_screen=`$.FROM`에 의존하는데, RN→상세는
      // deeplink 경유라 출처가 없으면 네이티브가 'DEEPLINK'로 폴백되어 집계에서 누락된다.
      // ⚠️ 전체 loggingAnalytics를 넣으면 Android(GoodsDetailDom)가 putAll로 SCREEN 등
      // 검색결과 파라미터를 상세 화면 기본값으로 덮어써 버린다. 그래서 FROM(+검색어)만
      // 최소로 싣는다. (iOS ProductNavigationParams는 FROM/SEARCH_KEYWORD만 추출)
      const fromScreen =
        typeof loggingAnalytics?.FROM === 'string'
          ? loggingAnalytics.FROM
          : screen || undefined;
      const fromContext: Record<string, unknown> | undefined = fromScreen
        ? {
            FROM: fromScreen,
            // 검색 유입 시 SEARCH_KEYWORD 귀속 유지(네이티브 기존 동작 패리티)
            ...(loggingAnalytics?.SEARCH_KEYWORD != null
              ? { SEARCH_KEYWORD: loggingAnalytics.SEARCH_KEYWORD }
              : {}),
          }
        : undefined;
      const fromContextJson = fromContext ? fromContext : undefined;

      const extras = {
        sno,
        ...(loggingAnalytics ? loggingAnalytics : {}),
        // RN 상품상세 라우트가 읽는 위치(extras.logging_analytics, JSON 문자열)
        ...(fromContextJson ? { logging_analytics: fromContextJson } : {}),
        ...(firstPageRendering ? firstPageRendering : {}),
        ...(couponSno ? { coupon_sno: couponSno } : {}),
      };
      // 네이티브 한정: 현재 화면이 RN 상품상세(GOODS_DETAIL)면 새 native surface 를
      // 띄우지 않고, 같은 surface 의 React Navigation 스택에 직접 push 한다.
      // goods→goods 브라우즈 체인이 단일 surface 안에서 처리되어, 인스턴스가 여러 개
      // 쌓이며 진입이 느려지던 현상(surface 생성 비용 + JS 스레드 경합)을 줄인다.
      // 다른 화면(home/search/market 등)에서의 진입은 기존대로 deeplink 로 새 surface 생성.
      if (!isWeb && experimentGroup) {
        return navigation.push('GoodsDetail', {
          instanceId,
          goodsId: sno,
          extras: {
            ...extras,
            ...(ad ? { ad } : {}),
          },
        });
      }

      const goodsDetailDeeplink = getDeeplink();
      appendParams(
        goodsDetailDeeplink,
        'extras',
        JSON.stringify({
          ...extras,
          ...(ad ? { ad: JSON.stringify(ad) } : {}),
        }),
      );
      // 네이티브 상품상세(ProductNavigationParams / Android GoodsDetailDom)는
      // top-level logging_analytics를 읽는다. FROM(+검색어) 최소 컨텍스트만 전달.
      if (fromContextJson) {
        appendParams(
          goodsDetailDeeplink,
          'logging_analytics',
          JSON.stringify(fromContextJson),
        );
      }

      if (isNotNil(remoteDeeplink)) {
        return bridge.openDeeplink(remoteDeeplink, {
          webCallback: async () => {
            const baseURL = await getWebBaseURL();
            location.href = `${baseURL}/${remoteDeeplink.replace('ably://webview?url=', '').replace('https://m.a-bly.com/', '')}`;
          },
        });
      }

      return bridge.openDeeplink(finalizeUrl(goodsDetailDeeplink), {
        webCallback: async () => {
          navigation.push('GoodsDetail', {
            instanceId,
            goodsId: sno,
            extras: JSON.stringify(extras),
          });
        },
      });
    },
    [isGoodsDetailRN, navigation, instanceId, screen],
  );

  // 연타 중복 진입 방지(네이티브 셀 탭 스로틀 정합). useThrottle 은 인자를 그대로 forward.
  return useThrottle(moveToGoods, MOVE_TO_GOODS_THROTTLE_MS);
};
